import { CheckBadgeIcon, FolderIcon, ArrowPathIcon, XMarkIcon } from "@heroicons/react/24/outline";
import { useMemo } from "react";
import { getFileIcon } from "src/helpers/documentHelper";

const StorageUploadProgress = ({ documentStatusList = [], onClose }) => {
  const treeData = useMemo(() => {
    if (documentStatusList?.length === 0) return {};

    const root = { name: "root", children: {}, isFolder: true };

    documentStatusList.forEach((item) => {
      const pathParts = item?.name?.split("/");
      let current = root;

      pathParts?.forEach((part, index) => {
        const isLast = index === pathParts?.length - 1;

        if (!current.children[part]) {
          current.children[part] = {
            name: part,
            children: {},
            isFolder: !isLast || item?.isFolder,
            status: isLast ? item?.status : "pending",
            extension: isLast ? item?.extension || part?.split(".").pop() : null,
          };
        }

        if (isLast) {
          current.children[part].status = item?.status;
          current.children[part].extension = item?.extension || part?.split(".").pop();
        }

        current = current.children[part];
      });
    });

    return root.children;
  }, [documentStatusList]);

  const renderTree = (nodes, level = 0) => {
    return Object.values(nodes).map((node, idx, arr) => {
      const isLastNode = idx === arr.length - 1;
      const isValidating = node.status === "validating";
      const isUploading = node.status === "uploading";
      const isSuccess = node.status === "success";
      const isFailed = node.status === "failed";

      const indentSize = level * 32;

      return (
        <div
          key={idx}
          className="relative flex flex-col">
          {level > 0 && (
            <div
              className="absolute bg-gray-200"
              style={{
                left: `${indentSize - 16}px`,
                top: 0,
                width: "1px",
                height: isLastNode ? "20px" : "100%",
              }}
            />
          )}

          <div
            className={`group relative flex items-center gap-3 rounded-lg px-3 py-2 transition-all duration-200 ${isUploading ? "bg-blue-50 shadow-sm ring-1 ring-blue-100" : "hover:bg-gray-50/80"}`}
            style={{ marginLeft: `${indentSize}px` }}>
            {level > 0 && (
              <div
                className="absolute bg-gray-200"
                style={{
                  left: "-16px",
                  top: "50%",
                  width: "16px",
                  height: "1px",
                }}
              />
            )}

            <div className="z-10 flex-shrink-0">
              {node.isFolder ? (
                <div className="flex h-6 w-6 items-center justify-center rounded-md bg-amber-100 text-amber-600 shadow-sm">
                  <FolderIcon className="h-4 w-4 fill-amber-500/20" />
                </div>
              ) : (
                getFileIcon(node.extension)
              )}
            </div>

            <div className="min-w-0 flex-1">
              <span className={`block truncate text-sm font-medium ${isSuccess ? "text-gray-400" : "text-gray-700"}`}>{node.name}</span>
            </div>

            <div className="flex-shrink-0">
              {(isValidating || isUploading) && (
                <div className="flex items-center gap-1.5 rounded-full bg-blue-100 px-2 py-1 text-[10px] font-semibold uppercase tracking-tight text-blue-700">
                  <ArrowPathIcon className="h-3 w-3 animate-spin" />
                  {isValidating ? "Validating..." : "Uploading..."}
                </div>
              )}
              {isSuccess && <CheckBadgeIcon className="h-5 w-5 text-emerald-500" />}
              {isFailed && <XMarkIcon className="h-5 w-5 text-red-500" />}
            </div>
          </div>

          {node.children && <div className="relative">{renderTree(node.children, level + 1)}</div>}
        </div>
      );
    });
  };

  const total = documentStatusList.length;
  const done = documentStatusList.filter((d) => d.status === "success").length;
  const progress = total > 0 ? (done / total) * 100 : 0;
  const isComplete = documentStatusList.every((d) => d.status === "success" || d.status === "failed");

  return (
    <div className="animate-fade-in flex h-full max-h-[70vh] flex-col bg-white">
      <div className="sticky top-0 z-20 mb-4 border-b border-gray-100 bg-white/80 px-2 backdrop-blur-md">
        <div className="mb-3 flex items-end justify-between">
          <div>
            <h3 className="text-base font-bold text-gray-900">{isComplete ? "Upload Complete" : "Uploading Assets..."}</h3>
            <p className="text-[11px] font-medium uppercase text-gray-500">
              {done} of {total} assets processed
            </p>
          </div>
          {isComplete && (
            <button
              onClick={onClose}
              className="rounded-lg bg-highlightColor px-4 py-2 text-xs font-bold text-white transition-transform hover:scale-105">
              Upload next batch
            </button>
          )}
        </div>
        <div className="h-2 w-full overflow-hidden rounded-full bg-gray-100">
          <div
            className="h-full rounded-full bg-highlightColor shadow-[0_0_8px_rgba(var(--color-primary),0.4)] transition-all duration-700 ease-in-out"
            style={{ width: `${progress}%` }}
          />
        </div>
      </div>

      <div className="custom-scrollbar flex-1 space-y-1 overflow-y-auto p-0.5 pr-2 pt-1">{renderTree(treeData)}</div>

      <div className="mt-4 flex items-center justify-between border-t border-gray-100 pt-4 text-[11px]">
        <div className="flex items-center gap-4 font-semibold uppercase tracking-widest text-gray-400">
          <span className="flex items-center gap-1.5">
            <div className="h-2 w-2 rounded-full bg-emerald-500 shadow-[0_0_5px_rgba(16,185,129,0.5)]" /> {done} Success
          </span>
          <span className="flex items-center gap-1.5">
            <div className="h-2 w-2 animate-pulse rounded-full bg-blue-500 shadow-[0_0_5px_rgba(59,130,246,0.5)]" />
            {documentStatusList.filter((d) => d.status === "uploading").length} Uploading..
          </span>
        </div>
      </div>
    </div>
  );
};

export default StorageUploadProgress;
